import React, { createContext, useContext, useState, useEffect } from 'react';

const MOBILE_BREAKPOINT = 768;

interface SidebarContextType {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [userManuallySet, setUserManuallySet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
      setUserManuallySet(true);
    }
  };

  const setSidebarCollapsedWithFlag = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    setUserManuallySet(true);
  };

  // Detect mobile and handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      
      // Close mobile drawer when switching to desktop
      if (!mobile && mobileOpen) {
        setMobileOpen(false);
      }
      
      // Auto-collapse on tablet/smaller desktop (only if not mobile and user hasn't manually set)
      if (!mobile && !userManuallySet) {
        setSidebarCollapsed(true);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userManuallySet, mobileOpen]);

  return (
    <SidebarContext.Provider value={{ 
      sidebarCollapsed, 
      setSidebarCollapsed: setSidebarCollapsedWithFlag, 
      toggleSidebar,
      isMobile,
      mobileOpen,
      setMobileOpen
    }}>
      {children}
    </SidebarContext.Provider>
  );
};
